/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";

import {
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { deleteUsedTools } from "src/graphql/mutations";

import AddUsedTool from "src/components/modals/AddUsedTool.react";
import { queryUsedTools } from "src/services/AmplifyQuery";
import Layout from "src/components/layouts/Layout.react";
import Card from "src/components/cards/Card.react";
import LoadingCard from "src/components/loading/LoadingCard.react";
import {
  Table,
  ColHeader,
  TableBody,
  Col,
} from "src/components/tables/InspectionTable.react";

import RemoveModal from "src/components/modals/RemoveModal.react";

/**
 * The Tools Page component
 *
 * @author Thais Watanabe
 */
const Tools = () => {
  const location = useLocation();

  const [tools, setTools] = useState([]);
  const [selectedTool, setSelectedTool] = useState(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const { inspection, turbineInspections } = location.state;
  const [isOpen, setIsOpen] = useState(false);

  /**
   * useEffect
   */
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  /* useEffect */
  useEffect(() => {
    fetchUsedTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchUsedTools
   */
  async function fetchUsedTools() {
    const filter = {
      inspectionID: { eq: inspection.id },
    };

    queryUsedTools(filter, setTools);
  }

  /**
   * showEdit
   * @param {*} tool
   */
  function showEdit(tool) {
    setSelectedTool(tool);
    setIsOpen(!isOpen);
  }

  /**
   * openRemoveDialog
   * @param {*} photo
   */
  async function openRemoveDialog(tool) {
    setSelectedTool(tool);
    setIsRemoveModalOpen(true);
  }

  /**
   * closeRemoveDialog
   */
  async function closeRemoveDialog() {
    setSelectedTool(null);
    setIsRemoveModalOpen(false);
  }

  /**
   * removeTool
   */
  async function removeTool() {
    try {
      await API.graphql(
        graphqlOperation(deleteUsedTools, {
          input: {
            id: selectedTool.id,
            _version: selectedTool._version,
          },
        })
      );

      const updatedTools = tools.filter((tool) => tool.id !== selectedTool.id);
      setTools(updatedTools);
      closeRemoveDialog();
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  return (
    <Layout
      title="使用工具一覧表"
      breadcrumbs={[
        "登録済点検一覧",
        "登録済点検一覧（号機）",
        "使用工具一覧表",
      ]}
    >
      <Card>
        <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap">
            <div className="ml-4 mt-2">
              <h3 className="text-md leading-6 font-medium text-gray-900">
                {inspection.name}
              </h3>
            </div>
            <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3">
              <button
                type="button"
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                onClick={() => setIsOpen(!isOpen)}
              >
                {/* <!-- Heroicon name: user-add  --> */}
                <PlusCircleIcon className="-ml-0.5 mr-2 h-4 w-4" />
                新規使用工具登録
              </button>
            </div>
          </div>
        </div>
      </Card>

      {!tools.length > 0 && <LoadingCard />}
      {tools.length > 0 && (
        <Card className="table-turbine-inspections">
          <Table>
            <thead>
              <tr>
                <ColHeader>No.</ColHeader>
                <ColHeader>管理番号</ColHeader>
                <ColHeader>機器名称</ColHeader>
                <ColHeader>メーカー</ColHeader>
                <ColHeader>型式番号</ColHeader>
                <ColHeader>シリアル番号</ColHeader>
                <ColHeader>校正有効期限</ColHeader>
                <ColHeader>使用号機</ColHeader>
                <ColHeader>使用前点検日</ColHeader>
                <ColHeader>使用後点検日</ColHeader>
                <ColHeader></ColHeader>
              </tr>
            </thead>
            <TableBody>
              {tools.map((usedTool, i) => (
                <tr key={i}>
                  <Col>{i + 1}</Col>
                  <Col>{usedTool.calibration.managementNumber}</Col>
                  <Col>{usedTool.calibration.tool.name}</Col>
                  <Col>{usedTool.calibration.maker.name}</Col>
                  <Col>{usedTool.calibration.modelNumber}</Col>
                  <Col>{usedTool.calibration.serialNumber}</Col>
                  <Col>{usedTool.calibration.expirationDate}</Col>
                  <Col>{usedTool.turbines}</Col>
                  <Col>{usedTool.dateStart}</Col>
                  <Col>{usedTool.dateEnd}</Col>
                  <Col>
                    <button
                      onClick={() => showEdit(usedTool)}
                      className="rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                    >
                      {/* <!-- Heroicon name: pencil-alt --> */}
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => openRemoveDialog(usedTool)}
                      className="rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                    >
                      {/* <!-- Heroicon name: trash --> */}
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </Col>
                </tr>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <AddUsedTool
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        tools={tools}
        setTools={setTools}
        selectedTool={selectedTool}
        setSelectedTool={setSelectedTool}
        inspection={inspection}
        turbineInspections={turbineInspections}
      />

      <RemoveModal
        open={isRemoveModalOpen}
        setOpen={setIsRemoveModalOpen}
        remove={removeTool}
      />
    </Layout>
  );
};

export default Tools;
